import {
  isCanonicalVisitorId,
  VISITOR_ID_COOKIE,
  VISITOR_ID_MAX_AGE_SECONDS,
} from "./visitorId.constants";

export { VISITOR_ID_COOKIE, VISITOR_ID_MAX_AGE_SECONDS } from "./visitorId.constants";

function readCookie(name: string): string | null {
  const match = document.cookie.split("; ").find((entry) => entry.startsWith(`${name}=`));
  const value = match?.slice(name.length + 1);
  return value ? decodeURIComponent(value) : null;
}

/**
 * First-party anonymous visitor identity, site-wide: one ID per browser, never scoped
 * to a practice context (practice rides in the URL path, not in the identity). The app
 * owns this ID; later phases hand it to GrowthBook for bucketing and to Segment as
 * anonymousId, and the visitor consent record keys on it. It must never be rotated by
 * app behavior (deploys, login/logout, consent changes, analytics resets) — only losing
 * browser storage may change it.
 *
 * The proxy mints the cookie server-side on the first eligible response; this
 * client-side mint is a fallback for any request that bypassed the proxy. A malformed
 * value is re-minted rather than adopted — both minting paths produce canonical UUIDs,
 * so anything else is foreign input the backend would reject anyway. Returns null
 * during SSR, where there is no document — callers gate on the client.
 */
export function getOrCreateVisitorId(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const existing = readCookie(VISITOR_ID_COOKIE);
  if (existing && isCanonicalVisitorId(existing)) {
    return existing;
  }

  const visitorId = crypto.randomUUID();
  const secure = window.location.protocol === "https:" ? ";secure" : "";
  document.cookie = `${VISITOR_ID_COOKIE}=${visitorId};path=/;max-age=${VISITOR_ID_MAX_AGE_SECONDS};samesite=lax${secure}`;
  return visitorId;
}
